// @ts-check
// Painel de propriedades do item selecionado e aba "Estilo" do editor de blocos (spec/email.md seção 19.2): bloco
// (texto, imagem, logo, botão, divisor, espaço, HTML), seção (layout, fundo, margens, empilhar no celular) e coluna
// (fundo, alinhamento vertical). Cada mudança é uma operação pura do documento.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { EMAIL_DOCUMENT_LIMITS, EMAIL_FONT_STACKS, EMAIL_SECTION_LAYOUTS } from '../../../../../shared/email-design/email-document.js';
import { findBlock } from '../../../../../shared/email-design/document-operations.js';
import { sanitizeEmailHtml } from '../../../../../shared/email-design/sanitize-email-html.js';
import { isValidImageUrl } from '../../../../../shared/email-design/email-links.js';
import { Button } from '../../../ui/button.js';
import { Icon } from '../../../ui/icon.js';
import { ChoiceField, ColorField, PaddingField, PropertyRow, RangeField, TextField, ToggleField, UrlField } from '../property-controls.js';
import { sanitizeReportSentence } from '../sanitize-report.js';
import { blockPaletteKey, SECTION_LAYOUT_KEYS } from './block-catalog.js';
import { isMockEnabled } from '../../../data/api-client.js';
import { t } from '../../../i18n/index.js';

/**
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailDocument} EmailDocument
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailBlock} EmailBlock
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailSection} EmailSection
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailStyle} EmailStyle
 * @typedef {import('../../../../../shared/email-design/email-document.js').EmailSectionLayout} EmailSectionLayout
 * @typedef {import('./block-canvas.js').EditorSelection} EditorSelection
 * @typedef {{
 *   document: EmailDocument, selection: EditorSelection, readOnly: boolean, brandColor: string,
 *   catalog: import('../../../data/email-templates.js').EmailVariableCatalog | null,
 *   updateBlock: (blockId: string, patch: Record<string, unknown>, coalesceKey?: string) => void,
 *   updateSection: (sectionId: string, patch: Record<string, unknown>, coalesceKey?: string) => void,
 *   setSectionLayout: (sectionId: string, layout: EmailSectionLayout) => void,
 *   updateColumn: (sectionId: string, columnIndex: number, patch: Record<string, unknown>, coalesceKey?: string) => void,
 *   openImageLibrary: (blockId: string) => void,
 *   select: (selection: EditorSelection) => void,
 *   problemsByBlock: Map<string, { errors: string[], warnings: string[] }>,
 * }} PropertiesEditor
 */

const ALIGN_OPTIONS = /** @type {const} */ ([
  { value: 'LEFT', icon: 'IconAlignLeft', key: 'left' },
  { value: 'CENTER', icon: 'IconAlignCenter', key: 'center' },
  { value: 'RIGHT', icon: 'IconAlignRight', key: 'right' },
]);

/** @param {{ layout: string }} props */
function LayoutPreview({ layout }) {
  const widths = EMAIL_SECTION_LAYOUTS[/** @type {EmailSectionLayout} */ (layout)] ?? [100];
  return html`<span class="email-layout-preview" aria-hidden="true">${widths.map((width) => html`<span style=${{ flexGrow: width }}></span>`)}</span>`;
}

/**
 * Problemas do bloco selecionado, em linha.
 * @param {{ problems: { errors: string[], warnings: string[] } | undefined }} props
 */
function BlockProblems({ problems }) {
  if (!problems || (problems.errors.length === 0 && problems.warnings.length === 0)) return null;
  return html`<ul class="email-block-problems">
    ${problems.errors.map((code) => html`<li data-severity="error"><${Icon} name="IconAlertTriangle" size=${14} />${t(`emailTemplates.problems.${code}`)}</li>`)}
    ${problems.warnings.map((code) => html`<li data-severity="warning"><${Icon} name="IconInfoCircle" size=${14} />${t(`emailTemplates.problems.${code}`)}</li>`)}
  </ul>`;
}

/**
 * Código do bloco HTML: rascunho local, saneado ao sair do campo, com o relatório embaixo.
 * @param {{ block: EmailBlock & { type: 'HTML' }, editor: PropertiesEditor }} props
 */
function HtmlBlockFields({ block, editor }) {
  const [draft, setDraft] = useState(block.html);
  const [report, setReport] = useState(/** @type {string} */ (''));
  useEffect(() => setDraft(block.html), [block.id]);
  const commit = () => {
    const result = sanitizeEmailHtml(draft, { profile: 'IMPORTED_FRAGMENT', allowDataImages: isMockEnabled() });
    setReport(sanitizeReportSentence(result.report));
    setDraft(result.html);
    if (result.html !== block.html) editor.updateBlock(block.id, { html: result.html });
  };
  return html`
    <${TextField} label=${t('emailDesign.html.code')} value=${draft} multiline=${true} monospace=${true} rows=${12} disabled=${editor.readOnly}
      maxLength=${EMAIL_DOCUMENT_LIMITS.htmlBlockCharacters} onChange=${setDraft} onBlur=${commit} hint=${t('emailDesign.html.blockHint')} />
    ${report ? html`<p class="email-sanitize-report" role="status"><${Icon} name="IconShieldCheck" size=${14} />${report}</p>` : null}
  `;
}

/**
 * Fonte da imagem: biblioteca, endereço da web ou o logo da escola.
 * @param {{ block: EmailBlock & { type: 'IMAGE' }, editor: PropertiesEditor }} props
 */
function ImageSourceFields({ block, editor }) {
  const source = block.source;
  const [urlDraft, setUrlDraft] = useState(source?.kind === 'URL' ? source.url : '');
  const [showUrl, setShowUrl] = useState(source?.kind === 'URL');
  useEffect(() => {
    setUrlDraft(source?.kind === 'URL' ? source.url : '');
    setShowUrl(source?.kind === 'URL');
  }, [block.id]);
  if (source?.kind === 'INSTITUTION_LOGO') {
    return html`<${PropertyRow} label=${t('emailDesign.properties.image')} hint=${t('emailDesign.properties.logoHint')}>
      <span class="email-property-value"><${Icon} name="IconBuildingCommunity" size=${14} />${t('emailDesign.palette.LOGO')}</span>
    <//>`;
  }
  const urlIsValid = urlDraft.trim() === '' || isValidImageUrl(urlDraft.trim());
  return html`<${PropertyRow} label=${t('emailDesign.properties.image')}>
    <div class="email-image-source">
      ${source?.kind === 'ASSET' ? html`<span class="email-property-value"><${Icon} name="IconPhoto" size=${14} />${t('emailDesign.properties.fromLibrary')}</span>` : null}
      <div class="email-button-row">
        <${Button} title=${t('emailDesign.properties.chooseImage')} icon="IconPhoto" size="small" variant="secondary" disabled=${editor.readOnly}
          onClick=${() => editor.openImageLibrary(block.id)} />
        <${Button} title=${t('emailDesign.images.useUrl')} icon="IconWorld" size="small" variant="tertiary" disabled=${editor.readOnly}
          onClick=${() => setShowUrl((value) => !value)} />
      </div>
      ${showUrl
        ? html`<input class="text-input" value=${urlDraft} placeholder="https://" disabled=${editor.readOnly} aria-label=${t('emailDesign.images.useUrl')}
            data-error=${urlIsValid ? undefined : 'true'}
            onInput=${(/** @type {Event} */ event) => setUrlDraft(/** @type {HTMLInputElement} */ (event.currentTarget).value)}
            onBlur=${() => {
              const url = urlDraft.trim();
              if (url && isValidImageUrl(url)) editor.updateBlock(block.id, { source: { kind: 'URL', url } });
            }} />
          <span class="input-hint" data-danger=${urlIsValid ? undefined : 'true'}>
            ${urlIsValid ? t('emailDesign.properties.imageUrlHint') : t('emailDesign.properties.imageUrlInvalid')}
          </span>`
        : null}
    </div>
  <//>`;
}

/**
 * @param {{ block: EmailBlock, editor: PropertiesEditor }} props
 */
function BlockFields({ block, editor }) {
  const disabled = editor.readOnly;
  /** @param {Record<string, unknown>} patch @param {string} [key] */
  const update = (patch, key) => editor.updateBlock(block.id, patch, key ? `${key}:${block.id}` : undefined);
  const limits = EMAIL_DOCUMENT_LIMITS;
  const alignField = (/** @type {'LEFT' | 'CENTER' | 'RIGHT'} */ value) => html`<${ChoiceField} label=${t('emailDesign.properties.align')} value=${value} iconOnly=${true} disabled=${disabled}
    options=${ALIGN_OPTIONS.map((option) => ({ value: option.value, icon: option.icon, label: t(`emailDesign.align.${option.key}`) }))}
    onChange=${(/** @type {string} */ next) => update({ align: next })} />`;
  const paddingField = 'padding' in block
    ? html`<${PaddingField} label=${t('emailDesign.properties.padding')} value=${block.padding} disabled=${disabled} onChange=${(/** @type {any} */ padding) => update({ padding })} />`
    : null;
  switch (block.type) {
    case 'TEXT':
      return html`<p class="email-property-note">${t('emailDesign.properties.textHint')}</p>${paddingField}`;
    case 'IMAGE':
      return html`
        <${ImageSourceFields} block=${block} editor=${editor} />
        ${block.source?.kind !== 'INSTITUTION_LOGO'
          ? html`<${TextField} label=${t('emailDesign.properties.alt')} value=${block.alt} maxLength=${limits.imageAltCharacters} disabled=${disabled}
              hint=${block.alt.trim() ? t('emailDesign.properties.altHint') : undefined} error=${block.alt.trim() ? null : t('emailDesign.properties.altMissing')}
              onChange=${(/** @type {string} */ alt) => update({ alt }, 'alt')} />`
          : null}
        <${ToggleField} label=${t('emailDesign.properties.fullWidth')} value=${block.width === 'FULL'} disabled=${disabled}
          onChange=${(/** @type {boolean} */ full) => update({ width: full ? 'FULL' : 300 })} />
        ${block.width !== 'FULL'
          ? html`<${RangeField} label=${t('emailDesign.properties.width')} value=${block.width} min=${limits.imageWidth.minimum} max=${limits.imageWidth.maximum} disabled=${disabled}
              onChange=${(/** @type {number} */ width) => update({ width }, 'width')} />`
          : null}
        ${alignField(block.align)}
        <${UrlField} label=${t('emailDesign.properties.imageLink')} value=${block.linkUrl ?? ''} catalog=${editor.catalog} disabled=${disabled}
          onChange=${(/** @type {string} */ value) => update({ linkUrl: value.trim() ? value : null }, 'linkUrl')} />
        ${paddingField}
      `;
    case 'BUTTON':
      return html`
        <${TextField} label=${t('emailDesign.properties.label')} value=${block.label} maxLength=${limits.buttonLabelCharacters} disabled=${disabled}
          error=${block.label.trim() ? null : t('emailDesign.properties.labelMissing')} onChange=${(/** @type {string} */ label) => update({ label }, 'label')} />
        <${UrlField} label=${t('emailDesign.properties.link')} value=${block.url} allowEmpty=${false} catalog=${editor.catalog} disabled=${disabled}
          onChange=${(/** @type {string} */ url) => update({ url }, 'url')} />
        <${ColorField} label=${t('emailDesign.properties.buttonColor')} value=${block.backgroundColor} allowBrand=${true} brandColor=${editor.brandColor} disabled=${disabled}
          onChange=${(/** @type {string} */ backgroundColor) => update({ backgroundColor }, 'buttonColor')} />
        <${ColorField} label=${t('emailDesign.properties.textColor')} value=${block.textColor} disabled=${disabled}
          onChange=${(/** @type {string} */ textColor) => update({ textColor }, 'textColor')} />
        <${ToggleField} label=${t('emailDesign.properties.fullWidth')} value=${block.fullWidth} disabled=${disabled} onChange=${(/** @type {boolean} */ fullWidth) => update({ fullWidth })} />
        <${RangeField} label=${t('emailDesign.properties.radius')} value=${block.borderRadius} min=${limits.buttonRadius.minimum} max=${limits.buttonRadius.maximum} disabled=${disabled}
          onChange=${(/** @type {number} */ borderRadius) => update({ borderRadius }, 'radius')} />
        <${RangeField} label=${t('emailDesign.properties.fontSize')} value=${block.fontSize} min=${limits.buttonFontSize.minimum} max=${limits.buttonFontSize.maximum} disabled=${disabled}
          onChange=${(/** @type {number} */ fontSize) => update({ fontSize }, 'fontSize')} />
        ${alignField(block.align)}
        ${paddingField}
      `;
    case 'DIVIDER':
      return html`
        <${ColorField} label=${t('emailDesign.properties.color')} value=${block.color} disabled=${disabled} onChange=${(/** @type {string} */ color) => update({ color }, 'color')} />
        <${RangeField} label=${t('emailDesign.properties.thickness')} value=${block.thickness} min=${limits.dividerThickness.minimum} max=${limits.dividerThickness.maximum} disabled=${disabled}
          onChange=${(/** @type {number} */ thickness) => update({ thickness }, 'thickness')} />
        <${RangeField} label=${t('emailDesign.properties.width')} unit="%" value=${block.widthPercent} min=${limits.dividerWidthPercent.minimum} max=${limits.dividerWidthPercent.maximum} disabled=${disabled}
          onChange=${(/** @type {number} */ widthPercent) => update({ widthPercent }, 'widthPercent')} />
        ${paddingField}
      `;
    case 'SPACER':
      return html`<${RangeField} label=${t('emailDesign.properties.height')} value=${block.height} min=${limits.spacerHeight.minimum} max=${limits.spacerHeight.maximum} disabled=${disabled}
        onChange=${(/** @type {number} */ height) => update({ height }, 'height')} />`;
    case 'HTML':
      return html`<${HtmlBlockFields} block=${block} editor=${editor} />`;
    default:
      return null;
  }
}

/**
 * @param {{ section: EmailSection, editor: PropertiesEditor }} props
 */
function SectionFields({ section, editor }) {
  const disabled = editor.readOnly;
  return html`
    <${PropertyRow} label=${t('emailDesign.properties.layout')}>
      <div class="email-layout-options" role="radiogroup" aria-label=${t('emailDesign.properties.layout')}>
        ${SECTION_LAYOUT_KEYS.map((layout) => html`<button type="button" role="radio" class="email-layout-option" disabled=${disabled}
          aria-checked=${section.layout === layout ? 'true' : 'false'} aria-label=${t(`emailDesign.layouts.${layout}`)} title=${t(`emailDesign.layouts.${layout}`)}
          onClick=${() => editor.setSectionLayout(section.id, layout)}>
          <${LayoutPreview} layout=${layout} />
        </button>`)}
      </div>
    <//>
    <${ColorField} label=${t('emailDesign.properties.background')} value=${section.backgroundColor} allowNone=${true} disabled=${disabled}
      onChange=${(/** @type {string | null} */ backgroundColor) => editor.updateSection(section.id, { backgroundColor }, `background:${section.id}`)} />
    <${PaddingField} label=${t('emailDesign.properties.padding')} value=${section.padding} disabled=${disabled}
      onChange=${(/** @type {any} */ padding) => editor.updateSection(section.id, { padding })} />
    ${section.columns.length > 1
      ? html`<${ToggleField} label=${t('emailDesign.properties.stackOnMobile')} value=${section.stackOnMobile} disabled=${disabled}
          hint=${t('emailDesign.properties.stackOnMobileHint')} onChange=${(/** @type {boolean} */ stackOnMobile) => editor.updateSection(section.id, { stackOnMobile })} />`
      : null}
  `;
}

/**
 * Painel do item selecionado (vazio: a dica).
 * @param {{ editor: PropertiesEditor }} props
 */
export function PropertiesPanel({ editor }) {
  const selection = editor.selection;
  if (!selection) {
    return html`<div class="email-properties-empty">
      <${Icon} name="IconClick" size=${20} />
      <span>${t('emailDesign.selectHint')}</span>
    </div>`;
  }
  if (selection.kind === 'BLOCK') {
    const block = findBlock(editor.document, selection.id);
    if (!block) return null;
    return html`<div class="email-properties" data-kind="block">
      <h3 class="email-properties-title">${t(`emailDesign.palette.${blockPaletteKey(block)}`)}</h3>
      <${BlockProblems} problems=${editor.problemsByBlock.get(block.id)} />
      <${BlockFields} key=${block.id} block=${block} editor=${editor} />
    </div>`;
  }
  if (selection.kind === 'SECTION') {
    const section = editor.document.sections?.find((item) => item.id === selection.id);
    if (!section) return null;
    return html`<div class="email-properties" data-kind="section">
      <h3 class="email-properties-title">${t('emailDesign.properties.section')}</h3>
      <${SectionFields} section=${section} editor=${editor} />
    </div>`;
  }
  const section = editor.document.sections?.find((item) => item.id === selection.sectionId);
  const column = section?.columns[selection.columnIndex];
  if (!section || !column) return null;
  return html`<div class="email-properties" data-kind="column">
    <h3 class="email-properties-title">${t('emailDesign.properties.column', { position: selection.columnIndex + 1 })}</h3>
    <${ColorField} label=${t('emailDesign.properties.background')} value=${column.backgroundColor} allowNone=${true} disabled=${editor.readOnly}
      onChange=${(/** @type {string | null} */ backgroundColor) => editor.updateColumn(section.id, selection.columnIndex, { backgroundColor }, `column:${column.id}`)} />
    <${ChoiceField} label=${t('emailDesign.properties.verticalAlign')} value=${column.verticalAlign} disabled=${editor.readOnly}
      options=${['TOP', 'MIDDLE', 'BOTTOM'].map((value) => ({ value, label: t(`emailDesign.verticalAlign.${value}`) }))}
      onChange=${(/** @type {string} */ verticalAlign) => editor.updateColumn(section.id, selection.columnIndex, { verticalAlign })} />
    <${Button} title=${t('emailDesign.properties.selectSection')} size="small" variant="tertiary" icon="IconLayoutRows"
      onClick=${() => editor.select({ kind: 'SECTION', id: section.id })} />
  </div>`;
}

/**
 * Aba "Estilo": cores, fonte e tamanho do e-mail todo.
 * @param {{ style: EmailStyle, onChange: (patch: Partial<EmailStyle>, coalesceKey?: string) => void, readOnly: boolean, brandColor: string,
 *   showLogoHeader?: boolean }} props
 */
export function StylePanel({ style, onChange, readOnly, brandColor, showLogoHeader = false }) {
  return html`<div class="email-properties" data-kind="style">
    <${ColorField} label=${t('emailDesign.properties.background')} value=${style.backgroundColor} disabled=${readOnly}
      onChange=${(/** @type {string} */ backgroundColor) => onChange({ backgroundColor }, 'style:background')} />
    <${ColorField} label=${t('emailDesign.properties.contentBackground')} value=${style.contentBackgroundColor} disabled=${readOnly}
      onChange=${(/** @type {string} */ contentBackgroundColor) => onChange({ contentBackgroundColor }, 'style:content')} />
    <${PropertyRow} label=${t('emailDesign.properties.fontFamily')} htmlFor="email-style-font">
      <select id="email-style-font" class="text-input email-select" value=${style.fontFamily} disabled=${readOnly}
        onChange=${(/** @type {Event} */ event) => onChange({ fontFamily: /** @type {any} */ (/** @type {HTMLSelectElement} */ (event.currentTarget).value) })}>
        ${Object.keys(EMAIL_FONT_STACKS).map((font) => html`<option value=${font} style=${{ fontFamily: EMAIL_FONT_STACKS[/** @type {keyof typeof EMAIL_FONT_STACKS} */ (font)] }}>${t(`emailDesign.fonts.${font}`)}</option>`)}
      </select>
    <//>
    <${RangeField} label=${t('emailDesign.properties.fontSize')} value=${style.fontSize} min=${EMAIL_DOCUMENT_LIMITS.fontSize.minimum} max=${EMAIL_DOCUMENT_LIMITS.fontSize.maximum}
      disabled=${readOnly} onChange=${(/** @type {number} */ fontSize) => onChange({ fontSize }, 'style:fontSize')} />
    <${ColorField} label=${t('emailDesign.properties.textColor')} value=${style.textColor} disabled=${readOnly}
      onChange=${(/** @type {string} */ textColor) => onChange({ textColor }, 'style:text')} />
    <${ColorField} label=${t('emailDesign.properties.linkColor')} value=${style.linkColor} allowBrand=${true} brandColor=${brandColor} disabled=${readOnly}
      onChange=${(/** @type {string} */ linkColor) => onChange({ linkColor }, 'style:link')} />
    ${showLogoHeader
      ? html`<${ToggleField} label=${t('emailDesign.properties.showLogoHeader')} value=${style.showLogoHeader} disabled=${readOnly}
          onChange=${(/** @type {boolean} */ value) => onChange({ showLogoHeader: value })} />`
      : null}
  </div>`;
}
